import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import moment from 'moment';
import Select from 'react-select';
import { getAllKaratForSelect } from 'pages/Componets/API_Modal/MasterAPI/KaratAPIModal';
import { getAllStoreForSelect } from 'pages/Componets/API_Modal/StoreMasterAPIModal';
import { createDiscountVoucher } from 'pages/Componets/API_Modal/DiscountAPI/DiscountVoucherAPIModal';
import notistack from 'pages/Core/Notistack';
import { downloadDiamondMakingChargesExcel } from 'pages/Componets/API_Modal/MakingChangesAPI/DiamondMakingChargesAPIModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ErrorModal from './ErrorModal';

interface AddDiscountVoucherProps {
    reloadDataSource(): void;
}

interface Type {
    text: string;
    value: number;
}

interface DiscountVoucherFormValues {
    storeIdList: number[];
    Importexcelfile: File | null;
    amount: string;
    minInvoiceAmt: string;
    isLifetime: boolean;
    voucherValueIsPercentage: boolean;
    fromDate: string | null;
    toDate: string | null;
    isAllStore: boolean;
    voucherType: string;
    withGst: boolean;
    isConsumerOfferApplicable: boolean;
    ktTypeIdList: number[];
}

const defaultValues: DiscountVoucherFormValues = {
    storeIdList: [],
    Importexcelfile: null,
    amount: "",
    minInvoiceAmt: "",
    isLifetime: false,
    voucherValueIsPercentage: false,
    fromDate: null,
    toDate: null,
    isAllStore: false,
    voucherType: "",
    withGst: false,
    isConsumerOfferApplicable: false,
    ktTypeIdList: [],
};

const AddDiscountVoucher: React.FC<AddDiscountVoucherProps> = ({ reloadDataSource }) => {
    const [loading, setLoading] = useState(false);
    const [storeAllData, setStoreAllData] = useState<Type[]>([]);
    const [karatAllData, setKaratAllData] = useState<Type[]>([]);
    const [showModal, setShowModal] = useState(false);
    const inputRef = useRef<any>(null);

    const {
        control,
        handleSubmit,
        reset,
        setFocus,
        watch,
        setValue,
        register,
        formState: { errors, isValid },
    } = useForm<DiscountVoucherFormValues>({
        defaultValues,
        mode: 'onBlur',
    });

    const isLifetime = watch('isLifetime');
    const fromDate = watch('fromDate');
    const isAllStore = watch('isAllStore');

    const karatSelectAllOption = { label: "Select All", value: "*" };
    const modifiedKaratOptions = [karatSelectAllOption, ...karatAllData.map(item => ({
        label: item.text,
        value: Number(item.value)
    }))];

    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const onSubmit = async (data: DiscountVoucherFormValues) => {
        try {
            setLoading(true);
            const formData = new FormData();

            formData.append('voucherValue', data.amount);
            formData.append('minInvoiceAmt', data.minInvoiceAmt);
            formData.append('isLifetime', String(data.isLifetime));
            formData.append('isAllStore', String(data.isAllStore));
            formData.append('voucherType', data.voucherType);
            formData.append('withGst', String(data.withGst));
            formData.append('isConsumerOfferApplicable', String(data.isConsumerOfferApplicable));

            if (data.fromDate) formData.append('fromDate', data.fromDate);
            if (data.toDate && !data.isLifetime) formData.append('toDate', data.toDate);

            data.storeIdList.forEach(storeId => {
                formData.append('storeIdList', storeId.toString());
            });

            data.ktTypeIdList.forEach(ktTypeId => {
                formData.append('ktTypeIdList', ktTypeId.toString());
            });

            if (data.Importexcelfile) {
                formData.append('Importexcelfile', data.Importexcelfile);
            }

            if (data.voucherValueIsPercentage) {
                formData.append('voucherValueIsPercentage', String(data.voucherValueIsPercentage));
            }

            const response = await createDiscountVoucher(formData);

            if (response?.status === 200) {
                notistack.notifyFromApi(response);
                reset();
                reloadDataSource();
                setShowModal(false);
                setValue("Importexcelfile", null);
                if (inputRef.current) inputRef.current.value = "";
            } else if (response?.status === 400 && response.data) {
                setErrorMessages(response.data || ["Failed to add entries."]);
                setShowErrorModal(true);
                setValue("Importexcelfile", null);
                if (inputRef.current) inputRef.current.value = "";
            } else {
                notistack.notifyFromApi(response)
                setValue("Importexcelfile", null);
                if (inputRef.current) inputRef.current.value = "";
            }

        } catch (error: any) {
            console.error("API Error:", error.message);
            notistack.error(error.message || "Failed to create discount voucher");
        } finally {
            setLoading(false);
        }
    };

    const getAllStoreDetails = async () => {
        try {
            const response = await getAllStoreForSelect();
            setStoreAllData(response?.data || []);
        } catch (error) {
            console.error("Error fetching store type data:", error);
            notistack.error("Failed to load store data");
        }
    };

    const getAllKaratTypeDetails = async () => {
        try {
            const response = await getAllKaratForSelect();
            setKaratAllData(response?.data || []);
        } catch (error) {
            console.error("Error fetching karat type data:", error);
            notistack.error("Failed to load karat data");
        }
    };

    useEffect(() => {
        getAllStoreDetails();
        getAllKaratTypeDetails();
    }, []);

    useEffect(() => {
        if (showModal) {
            setFocus('amount');
        }
    }, [showModal]);

    const handleDownloadsampleExcel = () => {
        const url = "samplefiles/DiscountVouchers.xlsx";
        const filename = "DiscountVouchers.xlsx";
        downloadDiamondMakingChargesExcel(url, filename);
    };

    const handleClose = () => {
        reset();
        setShowModal(false);
    };

    const handleOpen = () => {
        setShowModal(true);
    };

    return (
        <>
            <ErrorModal
                show={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                errors={errorMessages}
            />
            <div className="d-flex gap-2">
                <div className="d-flex justify-content-end">
                    <button className="btn btn-sm" style={{ border: "1px solid #000" }} onClick={handleDownloadsampleExcel}>
                        <FontAwesomeIcon icon={faFileExcel} className="me-1" style={{ color: '#217346' }} />
                        Sample Excel
                    </button>
                </div>
                <button className="btn btn-sm " style={{ border: "1px solid #000" }} onClick={handleOpen}>
                    <i className="bi bi-plus-lg" /> Add Discount Voucher
                </button>
            </div>
            <Modal show={showModal} onHide={handleClose} backdrop="static" size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add Discount Voucher</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="col-md-6">
                                <label className="form-label">
                                    Upload Excel File <span style={{ color: "red" }}>*</span>
                                </label>
                                <Controller
                                    name="Importexcelfile"
                                    control={control}
                                    rules={{
                                        required: "Excel file is required",
                                        validate: (value) => {
                                            if (value && !["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"].includes(value.type)) {
                                                return "Only Excel files are allowed";
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field }) => (
                                        <input
                                            ref={inputRef}
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={(e) => field.onChange(e.target.files?.[0] || null)}
                                            className={`form-control form-control-sm ${errors.Importexcelfile ? 'is-invalid' : ''}`}
                                        />
                                    )}
                                />
                                {errors.Importexcelfile && (
                                    <div className="text-danger small mt-1">{errors.Importexcelfile.message}</div>
                                )}
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">
                                    {watch("voucherValueIsPercentage")
                                        ? "Percentage Discount (%)"
                                        : "Discount Amount"}{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <Controller
                                    name="amount"
                                    control={control}
                                    rules={{
                                        required: "Value is required",
                                        validate: (value) => {
                                            const numValue = Number(value);
                                            if (isNaN(numValue)) return "Must be a number";
                                            if (numValue <= 0) return "Must be positive";
                                            if (watch("voucherValueIsPercentage") && numValue > 100)
                                                return "Percentage cannot exceed 100";
                                            if (!/^\d{1,5}(\.\d{1,2})?$/.test(value)) {
                                                return "Up to 5 digits and 2 decimals allowed";
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field }) => (
                                        <input
                                            type="text"
                                            placeholder={
                                                watch("voucherValueIsPercentage")
                                                    ? "Enter percentage"
                                                    : "Enter amount"
                                            }
                                            className={`form-control form-control-sm ${errors.amount ? "is-invalid" : ""
                                                }`}
                                            {...field}
                                            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                let value = e.target.value.replace(/[^0-9.]/g, "");
                                                const parts = value.split(".");
                                                if (parts.length > 2) value = parts[0] + "." + parts[1];
                                                if (parts[0].length > 5) parts[0] = parts[0].slice(0, 5);
                                                if (parts[1] && parts[1].length > 2)
                                                    parts[1] = parts[1].slice(0, 2);
                                                value = parts.join(".");
                                                e.target.value = e.target.value.replace(/\s/g, "");
                                                field.onChange(value);
                                            }}
                                        />
                                    )}
                                />

                                {errors.amount && (
                                    <div className="text-danger small mt-1">{errors.amount.message}</div>
                                )}
                            </div>

                            <div className="col-md-6 mt-3">
                                <label className="form-label">
                                    Min Invoice Amount <span style={{ color: "red" }}>*</span>
                                </label>
                                <Controller
                                    name="minInvoiceAmt"
                                    control={control}
                                    rules={{
                                        required: "Minimum invoice amount is required",
                                        validate: (value) => {
                                            const numValue = Number(value);
                                            if (isNaN(numValue)) return "Minimum Invoice Amount must be a number";
                                            if (numValue <= 0) return "Minimum Invoice Amount must be positive";
                                            if (!/^\d{1,5}(\.\d{1,2})?$/.test(value)) {
                                                return "Enter up to 5 digits and 2 decimals";
                                            }
                                            return true;
                                        },
                                    }}
                                    render={({ field }) => (
                                        <input
                                            type="text"
                                            autoComplete="off"
                                            placeholder="Enter min invoice amount"
                                            {...field}
                                            onKeyDown={(e) => {
                                                const allowedKeys = [
                                                    "Backspace",
                                                    "Delete",
                                                    "ArrowLeft",
                                                    "ArrowRight",
                                                    "Tab",
                                                ];

                                                // allow Ctrl/Command shortcuts
                                                if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(e.key)) {
                                                    return;
                                                }

                                                // allow one decimal point
                                                if (e.key === "." && !field.value.includes(".")) {
                                                    return;
                                                }

                                                // block everything except digits, allowed keys, and one dot
                                                if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                let value = e.target.value;

                                                // sanitize just in case of paste
                                                value = value.replace(/[^0-9.]/g, "");

                                                const parts = value.split(".");
                                                if (parts.length > 2) {
                                                    value = parts[0] + "." + parts[1];
                                                }
                                                if (parts[0].length > 5) {
                                                    parts[0] = parts[0].slice(0, 5);
                                                }
                                                if (parts[1] && parts[1].length > 2) {
                                                    parts[1] = parts[1].slice(0, 2);
                                                }

                                                value = parts.join(".");
                                                e.target.value = e.target.value.replace(/\s/g, "");
                                                field.onChange(value);
                                            }}
                                            className={`form-control form-control-sm ${errors.minInvoiceAmt ? "is-invalid" : ""
                                                }`}
                                        />
                                    )}
                                />


                                {errors.minInvoiceAmt && (
                                    <div className="text-danger small mt-1">{errors.minInvoiceAmt.message}</div>
                                )}
                            </div>

                            <div className="col-md-6 mt-3">
                                <label className="form-label">
                                    Voucher Type <span style={{ color: "red" }}>*</span>
                                </label>
                                <Controller
                                    name="voucherType"
                                    control={control}
                                    rules={{ required: "Voucher type is required" }}
                                    render={({ field }) => (
                                        <input
                                            type="text"
                                            maxLength={30}
                                            autoComplete="off"
                                            placeholder="Enter voucher type"
                                            {...field}
                                            className={`form-control form-control-sm ${errors.voucherType ? 'is-invalid' : ''}`}
                                            onInput={(e: any) => {
                                                e.target.value = e.target.value.replace(/\s/g, "");
                                            }}
                                        />
                                    )}
                                />
                                {errors.voucherType && (
                                    <div className="text-danger small mt-1">{errors.voucherType.message}</div>
                                )}
                            </div>

                            <div className="col-md-6 mt-3">
                                <label className="form-label">
                                    Karat Type <span style={{ color: "red" }}>*</span>
                                </label>
                                <Controller
                                    name="ktTypeIdList"
                                    control={control}
                                    rules={{
                                        required: "Please select at least one karat type",
                                        validate: (value) => value && value.length > 0 || "Please select at least one karat type"
                                    }}
                                    render={({ field }) => {
                                        const selectedValues = (field.value || [])
                                            .map((val: number) =>
                                                modifiedKaratOptions.find((opt) => opt.value === val)
                                            )
                                            .filter(Boolean);

                                        const visibleValue = selectedValues[0];
                                        const hasError = errors.ktTypeIdList; // Check if there's an error

                                        return (
                                            <div className={`position-relative ${hasError ? 'is-invalid' : ''}`}>
                                                <Select
                                                    options={modifiedKaratOptions}
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    hideSelectedOptions={false}
                                                    placeholder="--Select karat type--"
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    value={selectedValues}
                                                    getOptionLabel={(e: any) => e.label}
                                                    getOptionValue={(e: any) => e.value}
                                                    onChange={(selected: any[]) => {
                                                        if (!selected) {
                                                            field.onChange([]);
                                                            return;
                                                        }

                                                        const isSelectAllSelected = selected.some(
                                                            (option) => option.value === "*"
                                                        );

                                                        if (isSelectAllSelected) {
                                                            field.onChange(modifiedKaratOptions.slice(1).map((opt) => opt.value));
                                                            document.activeElement instanceof HTMLElement &&
                                                                document.activeElement.blur();
                                                        } else if (
                                                            selected.length === karatAllData.length &&
                                                            field.value?.length !== karatAllData.length
                                                        ) {
                                                            field.onChange(modifiedKaratOptions.slice(1).map((opt) => opt.value));
                                                        } else {
                                                            field.onChange(selected.map((opt) => opt.value));
                                                        }
                                                    }}
                                                    styles={{
                                                        control: (provided: any, state: any) => ({
                                                            ...provided,
                                                            height: '30px',
                                                            minHeight: '30px',
                                                            fontSize: '13px',
                                                            backgroundColor: '#fff',
                                                            alignItems: 'center',
                                                            borderColor: hasError ? '#dc3545' : (state.isFocused ? '#80bdff' : '#ced4da'), // Apply red border when error
                                                            '&:hover': {
                                                                borderColor: '#515151ff',
                                                            },
                                                        }),
                                                        menu: (base: any) => ({
                                                            ...base,
                                                            zIndex: 9999,
                                                            marginTop: '0.25rem',
                                                            border: '1px solid rgba(0, 0, 0, 0.1)',
                                                        }),
                                                        option: (provided: any, state: any) => ({
                                                            ...provided,
                                                            padding: '8px 12px',
                                                            fontSize: '14px',
                                                            color: state.isSelected ? '#1a1a1a' : '#1a1a1a',
                                                            backgroundColor: state.isSelected
                                                                ? '#f8f8f8'
                                                                : state.isFocused
                                                                    ? '#f8f9fa'
                                                                    : '#fff',
                                                            '&:active': {
                                                                backgroundColor: '#dadada',
                                                            },
                                                        }),
                                                        multiValue: (base: any, state: any) => {
                                                            if (selectedValues.length > 1 && state.data.label !== visibleValue?.label) {
                                                                return { ...base, display: 'none' };
                                                            }
                                                            return {
                                                                ...base,
                                                                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                                                maxHeight: '24px',
                                                            };
                                                        },
                                                        multiValueLabel: (base: any, state: any) => {
                                                            if (selectedValues.length > 1 && state.data.label !== visibleValue?.label) {
                                                                return { ...base, display: 'none' };
                                                            }
                                                            return {
                                                                ...base,
                                                                color: 'black',
                                                                fontWeight: '500',
                                                                padding: '2px 6px',
                                                                fontSize: '12px',
                                                            };
                                                        },
                                                        multiValueRemove: (base: any, state: any) => {
                                                            if (selectedValues.length > 1 && state.data.label !== visibleValue?.label) {
                                                                return { ...base, display: 'none' };
                                                            }
                                                            return {
                                                                ...base,
                                                                color: 'black',
                                                                ':hover': {
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                                    color: 'black',
                                                                },
                                                            };
                                                        },
                                                        valueContainer: (base: any) => ({
                                                            ...base,
                                                            padding: '0 8px',
                                                            height: '30px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        }),
                                                        indicatorsContainer: (base: any) => ({
                                                            ...base,
                                                            height: '30px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            paddingRight: '4px',
                                                        }),
                                                        dropdownIndicator: (base: any) => ({
                                                            ...base,
                                                            padding: '4px',
                                                            color: '#6c757d',
                                                            height: '30px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            ':hover': {
                                                                color: '#495057',
                                                            },
                                                        }),
                                                        clearIndicator: (base: any) => ({
                                                            ...base,
                                                            padding: '4px',
                                                            color: '#6c757d',
                                                            height: '30px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            ':hover': {
                                                                color: '#495057',
                                                            },
                                                        }),
                                                    }}
                                                />
                                                {selectedValues.length > 1 && (
                                                    <span
                                                        className="translate-middle-y end-0 me-3 badge rounded-pill bg-light text-primary border border-primary"
                                                        style={{
                                                            fontSize: "11px",
                                                            fontWeight: "500",
                                                            padding: "3px 6px",
                                                        }}
                                                    >
                                                        +{selectedValues.length - 1} more
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                                {errors.ktTypeIdList && (
                                    <div className="text-danger small mt-1">{errors.ktTypeIdList.message}</div>  // Display error message
                                )}
                            </div>


                            {/* {!isAllStore && (
                                <div className="col-md-6 mt-3">
                                    <label className="form-label">
                                        Stores <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <Controller
                                        name="storeIdList"
                                        control={control}
                                        rules={{
                                            validate: (value) => {
                                                if (isAllStore) return true;
                                                return value && value.length > 0 || "Please select at least one store";
                                            }
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                options={storeAllData.map(item => ({
                                                    label: item.text,
                                                    value: item.value
                                                }))}
                                                isMulti
                                                value={field.value?.map(val => ({
                                                    label: storeAllData.find(store => store.value === val)?.text || '',
                                                    value: val
                                                }))}
                                                onChange={(selected: any) => {
                                                    field.onChange(selected?.map((item: any) => item.value) || []);
                                                }}
                                                className={`react-select-container ${errors.storeIdList ? 'is-invalid' : ''}`}
                                                classNamePrefix="react-select"
                                            />
                                        )}
                                    />
                                    {errors.storeIdList && (
                                        <div className="text-danger small mt-1">{errors.storeIdList.message}</div>
                                    )}
                                </div>
                            )} */}

                            <div className="col-md-6 mt-3">
                                <label className="form-label">
                                    From Date <span style={{ color: "red" }}>*</span>
                                </label>
                                <Controller
                                    name="fromDate"
                                    control={control}
                                    rules={{
                                        required: "From date is required",
                                        validate: (value) => {
                                            if (moment(value).isBefore(moment(), 'day')) {
                                                return "From date cannot be before today";
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field }) => (
                                        <input
                                            type="date"
                                            className={`form-control form-control-sm ${errors.fromDate ? 'is-invalid' : ''}`}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            min={moment().format('YYYY-MM-DD')}
                                        />
                                    )}
                                />
                                {errors.fromDate && (
                                    <div className="text-danger small mt-1">{errors.fromDate.message}</div>
                                )}
                            </div>

                            {!isLifetime && (
                                <div className="col-md-6 mt-3">
                                    <label className="form-label">
                                        To Date <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <Controller
                                        name="toDate"
                                        control={control}
                                        rules={{
                                            required: "To date is required",
                                            validate: (value) => {
                                                if (fromDate && moment(value).isBefore(moment(fromDate), 'day')) {
                                                    return "To date cannot be before From date";
                                                }
                                                return true;
                                            }
                                        }}
                                        render={({ field }) => (
                                            <input
                                                type="date"
                                                className={`form-control form-control-sm ${errors.toDate ? 'is-invalid' : ''}`}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                min={fromDate || moment().format('YYYY-MM-DD')}
                                            />
                                        )}
                                    />
                                    {errors.toDate && (
                                        <div className="text-danger small mt-1">{errors.toDate.message}</div>
                                    )}
                                </div>
                            )}

                            <div className="col-md-12 d-flex flex-wrap gap-3 mt-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="voucherValueIsPercentage"
                                        {...register("voucherValueIsPercentage")}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="voucherValueIsPercentage"
                                    >
                                        Is Percentage
                                    </label>
                                </div>
                                <div className="form-check">
                                    <Controller
                                        name="isLifetime"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="isLifetime"
                                                checked={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e.target.checked);
                                                    if (e.target.checked) setValue("toDate", null);
                                                }}
                                            />
                                        )}
                                    />
                                    <label className="form-check-label" htmlFor="isLifetime">
                                        Is Lifetime
                                    </label>
                                </div>

                                <div className="form-check">
                                    <Controller
                                        name="isAllStore"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="isAllStore"
                                                checked={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e.target.checked);
                                                    if (e.target.checked) setValue("storeIdList", []);
                                                }}
                                            />
                                        )}
                                    />
                                    <label className="form-check-label" htmlFor="isAllStore">
                                        Is All Store
                                    </label>
                                </div>

                                <div className="form-check">
                                    <Controller
                                        name="withGst"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="withGst"
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label className="form-check-label" htmlFor="withGst">
                                        With GST
                                    </label>
                                </div>

                                <div className="form-check">
                                    <Controller
                                        name="isConsumerOfferApplicable"
                                        control={control}
                                        render={({ field }) => (
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="isConsumerOfferApplicable"
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        )}
                                    />
                                    <label className="form-check-label" htmlFor="isConsumerOfferApplicable">
                                        Consumer Offer Applicable
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2 justify-content-end mt-4 mb-2">
                            <button type="button" className="btn add-btn" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn submit-btn"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Add'}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AddDiscountVoucher;